<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="a"
                :pages="[
                    {
                        name: $tc('messages.gamification_sets', 2),
                        href: meta.links.set_index,
                    },
                    {
                        name: gamificationSet.breadcrumb_title,
                        href: meta.links.set_show,
                    },
                    {
                        name: $tc('messages.gamification_rewards', 2),
                        href: meta.links.index,
                    },
                    {
                        name: $t('messages.edit'),
                        href: null,
                    },
                ]"
            />
        </template>

        <form :action="meta.links.update" method="post" @submit.prevent="submit">
            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.information') }}

                    <div class="flex flex-col w-40">
                        <div class="text-sm">
                            <div class="flex space-x-2">
                                <label> &nbsp;{{ rewardPreview.title }} </label>
                            </div>
                        </div>
                        <div class="mt-2">
                            <div
                                :class="[rewardPreview.is_negative ? 'bg-red-500' : 'bg-indigo-500']"
                                class="shrink-0 flex items-center justify-center h-16 w-full rounded-lg aspect-w-1 aspect-h-1"
                            >
                                <img v-if="reward.image_path" :src="reward.image_path" alt="" />
                            </div>
                        </div>
                        <div class="flex justify-between mt-2">
                            <div>
                                <BadgePill
                                    color="green"
                                    class="text-lg"
                                    v-if="rewardPreview.experience_amount !== null"
                                >
                                    {{ rewardPreview.experience_amount }}
                                </BadgePill>
                            </div>
                            <div>
                                <BadgePill color="red" class="text-lg" v-if="rewardPreview.health_amount !== null">
                                    {{ rewardPreview.health_amount }}
                                </BadgePill>
                            </div>
                            <div>
                                <BadgePill color="yellow" class="text-lg" v-if="rewardPreview.coins_amount !== null">
                                    {{ rewardPreview.coins_amount }}
                                </BadgePill>
                            </div>
                        </div>
                    </div>
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.title')" for="title" />
                            <FormInput
                                required
                                id="title"
                                v-model="form.title"
                                name="title"
                                type="text"
                                max="191"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.title" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-12">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.description')" for="description">
                                <template v-slot:corner>
                                    <CharCounter
                                        :value="form.description"
                                        :max="240"
                                        show-max
                                        class="text-sm font-medium leading-5 text-gray-500"
                                    />
                                </template>
                            </FormLabel>
                            <FormTextarea
                                id="description"
                                v-model="form.description"
                                name="description"
                                type="text"
                                max="240"
                                class="mt-1"
                            />
                            <FormError :value="form.errors.description" class="mt-2" />
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $tc('messages.points', 2) }}
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.experience_amount')" for="experience_amount" />
                            <FormInput
                                id="experience_amount"
                                v-model.number="form.experience_amount"
                                min="1"
                                name="experience_amount"
                                type="number"
                                class="mt-1"
                            />
                            <FormError :value="form.errors.experience_amount" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.health_amount')" for="health_amount" />
                            <FormInput
                                id="health_amount"
                                v-model.number="form.health_amount"
                                name="health_amount"
                                type="number"
                                class="mt-1"
                            />
                            <FormError :value="form.errors.health_amount" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.coins_amount')" for="coins_amount" />
                            <FormInput
                                id="coins_amount"
                                v-model.number="form.coins_amount"
                                name="coins_amount"
                                type="number"
                                class="mt-1"
                            />
                            <FormError :value="form.errors.coins_amount" class="mt-2" />
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.other') }}
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.is_negative')" for="is_negative" />
                            <input
                                name="selected"
                                v-model="form.is_negative"
                                type="checkbox"
                                class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                            />
                            <FormError :value="form.errors.is_negative" class="mt-2" />
                        </div>
                    </div>
                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.max_one_per_day')" for="max_one_per_day" />
                            <input
                                name="selected"
                                v-model="form.max_one_per_day"
                                type="checkbox"
                                class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                            />
                            <FormError :value="form.errors.max_one_per_day" class="mt-2" />
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <TitleFieldsFormSection :class="form.is_negative ? 'visible' : 'invisible'">
                <template v-slot:title>
                    {{ $tc('messages.fouls', 1) }}
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-12">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.creates_foul')" for="creates_foul" />
                            <input
                                name="selected"
                                v-model="form.creates_foul"
                                type="checkbox"
                                class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                            />
                            <FormError :value="form.errors.creates_foul" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-4" :class="form.creates_foul ? 'visible' : 'invisible'">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.type')" for="type" />
                            <FormSelect
                                id="type"
                                :required="form.creates_foul"
                                v-model="form.type"
                                :options="options.types"
                                class="w-full mt-1"
                            />
                            <FormError :value="form.errors.type" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-4" :class="form.creates_foul ? 'visible' : 'invisible'">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.severity')" for="severity" />
                            <FormSelect
                                id="severity"
                                :required="form.creates_foul"
                                v-model="form.severity"
                                :options="options.severities"
                                class="w-full mt-1"
                            />
                            <FormError :value="form.errors.severity" class="mt-2" />
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.gamification_requests') }}
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-3">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.is_requestable')" for="is_requestable" />
                            <input
                                id="is_requestable"
                                type="checkbox"
                                v-model="form.is_requestable"
                                name="is_requestable"
                                class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                            />
                            <FormError :value="form.errors.is_requestable" class="mt-2" />
                        </div>
                    </div>
                    <div class="sm:col-span-4" v-if="form.is_requestable">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.max_request_per_day')" for="max_request_per_day" />
                            <FormInput
                                id="max_request_per_day"
                                v-model.number="form.max_request_per_day"
                                name="max_request_per_day"
                                type="number"
                                class="mt-1"
                                :required="form.is_requestable"
                                max="200"
                            />
                            <FormError :value="form.errors.max_request_per_day" class="mt-2" />
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <div class="flex justify-end mt-5">
                <SubmitFormButton :button-text="$t('messages.save')" :processing="form.processing" />
            </div>
        </form>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import TitleFieldsFormSection from '@/components/TitleFieldsFormSection.vue';
import FormInput from '@/components/Forms/FormInput.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormError from '@/components/Forms/FormError.vue';
import FormSelect from '@/components/Forms/FormSelect.vue';
import BadgePill from '@/components/BadgePill.vue';
import FormTextarea from '@/components/Forms/FormTextarea.vue';
import CharCounter from '@/components/Forms/CharCounter.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';

export default {
    components: {
        SubmitFormButton,
        PanelPage,
        TitleFieldsFormSection,
        FormInput,
        FormLabel,
        FormError,
        FormSelect,
        FormTextarea,
        CharCounter,
        BadgePill,
    },
    layout: Layout,
    name: 'Edit',
    props: ['meta', 'options', 'reward', 'gamificationSet'],
    data() {
        return {
            form: this.$inertia.form({
                title: this.reward.title,
                description: this.reward.description,
                experience_amount: this.reward.experience_amount,
                coins_amount: this.reward.coins_amount,
                health_amount: this.reward.health_amount,
                is_negative: this.reward.is_negative,
                creates_foul: this.reward.creates_foul,
                type: this.reward.type,
                severity: this.reward.severity,
                max_one_per_day: this.reward.max_one_per_day,
                is_requestable: this.reward.is_requestable,
                max_request_per_day: this.reward.max_request_per_day,
            }),
        };
    },
    watch: {
        'form.is_requestable': function (newVal) {
            if (newVal === false) {
                this.form.max_request_per_day = 0;
            }
        },
    },
    computed: {
        rewardPreview: function () {
            return {
                title: this.form.title,
                is_negative: this.form.is_negative,
                experience_amount: this.form.experience_amount ? this.form.experience_amount : null,
                coins_amount: this.form.coins_amount ? this.form.coins_amount : null,
                health_amount: this.form.health_amount ? this.form.health_amount : null,
            };
        },
    },
    methods: {
        submit() {
            this.form.patch(this.meta.links.update);
        },
    },
};
</script>
